import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * A modal confirmation for a destructive action.
 *
 * Rendered in a portal at the document root rather than inline, so asking the
 * question never reflows the page behind it — an inline confirm that expands a
 * card shifts everything below it, which is exactly the moment you least want
 * the thing you were aiming at to move.
 *
 * Deliberately not `window.confirm`: a native dialog cannot say what is
 * actually at stake, and here that matters. Removing an opening keeps its
 * progress records, so the honest message is "you can add it back and carry
 * on", which turns a frightening decision into a reversible one.
 *
 * Escape cancels, the backdrop cancels, and focus moves to the cancel button
 * rather than the destructive one — a stray Enter should not delete anything.
 */
export default function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  danger = true,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    // Stop the page behind scrolling while the dialog is up.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onCancel]);

  return createPortal(
    <div className="modal-backdrop" onClick={onCancel} role="presentation">
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="modal-title" id="confirm-title">
          {title}
        </h2>
        <div className="modal-body">{body}</div>
        <div className="modal-actions">
          <button type="button" className="btn" ref={cancelRef} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
